import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, BookOpen, Users, CheckCircle, Send } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import {
  PageHeader, AppCard, AppCardHeader, AppCardTitle, AppCardContent, EmptyState,
} from '../ui';
import CommonToaster from '../Common/CommonToaster';
import './StudentFeedbackPage.scss';

/* ─── Star Rating Input ─── */
function StarRating({ value, onChange, disabled }) {
  const [hover, setHover] = useState(0);

  return (
    <div className="sfb-stars">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`sfb-star ${star <= (hover || value) ? 'sfb-star--filled' : ''} ${disabled ? 'sfb-star--disabled' : ''}`}
          onClick={() => !disabled && onChange(star)}
          onMouseEnter={() => !disabled && setHover(star)}
          onMouseLeave={() => setHover(0)}
          disabled={disabled}
          aria-label={`${star} star`}
        >
          <Star size={22} />
        </button>
      ))}
      {value > 0 && <span className="sfb-stars__label">{value}/5</span>}
    </div>
  );
}

/* ─── Read-only Star Display ─── */
function StarDisplay({ value }) {
  return (
    <div className="sfb-stars">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={`sfb-star sfb-star--display ${star <= value ? 'sfb-star--filled' : ''}`}>
          <Star size={18} />
        </span>
      ))}
      <span className="sfb-stars__label">{value}/5</span>
    </div>
  );
}

/* ─── Question Row ─── */
function QuestionRow({ question, value, onChange, disabled }) {
  return (
    <div className="sfb-question">
      <p className="sfb-question__text">{question}</p>
      {disabled ? <StarDisplay value={value} /> : <StarRating value={value} onChange={onChange} disabled={disabled} />}
    </div>
  );
}

const TRAINER_QUESTIONS = [
  { key: 'trainerTeachingQuality', label: 'How would you rate the trainer\'s teaching quality?' },
  { key: 'trainerClarity', label: 'How clearly did the trainer explain concepts?' },
  { key: 'trainerDoubtHandling', label: 'How well were your doubts/questions handled?' },
  { key: 'trainerEngagement', label: 'How engaging were the sessions?' },
];

const COURSE_QUESTIONS = [
  { key: 'courseContent', label: 'How would you rate the overall course content?' },
  { key: 'coursePractice', label: 'How useful were the assignments/practice?' },
  { key: 'courseRelevance', label: 'How relevant was the course to real-world/industry needs?' },
  { key: 'courseDifficulty', label: 'Was the course difficulty level appropriate?' },
];

const StudentFeedbackPage = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [batchInfo, setBatchInfo] = useState(null);
  const [courseInfo, setCourseInfo] = useState(null);
  const [existingFeedback, setExistingFeedback] = useState(null);

  // Form state
  const [ratings, setRatings] = useState({
    trainerTeachingQuality: 0,
    trainerClarity: 0,
    trainerDoubtHandling: 0,
    trainerEngagement: 0,
    courseContent: 0,
    coursePractice: 0,
    courseRelevance: 0,
    courseDifficulty: 0,
  });
  const [suggestions, setSuggestions] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get('/feedback/my');
        setBatchInfo(data.batch);
        setCourseInfo(data.course);
        if (data.feedback) {
          setExistingFeedback(data.feedback);
          setRatings({
            trainerTeachingQuality: data.feedback.trainerTeachingQuality || 0,
            trainerClarity: data.feedback.trainerClarity || 0,
            trainerDoubtHandling: data.feedback.trainerDoubtHandling || 0,
            trainerEngagement: data.feedback.trainerEngagement || 0,
            courseContent: data.feedback.courseContent || 0,
            coursePractice: data.feedback.coursePractice || 0,
            courseRelevance: data.feedback.courseRelevance || 0,
            courseDifficulty: data.feedback.courseDifficulty || 0,
          });
          setSuggestions(data.feedback.suggestions || '');
        }
      } catch {
        setToast({ show: true, message: 'Failed to load feedback data.', type: 'error' });
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const setRating = (key, val) => {
    setRatings((prev) => ({ ...prev, [key]: val }));
  };

  const courseRatingsValid = COURSE_QUESTIONS.every((q) => ratings[q.key] > 0);

  const handleSubmit = async () => {
    if (!courseRatingsValid) {
      setToast({ show: true, message: 'Please rate all course questions.', type: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...ratings, suggestions };
      // Send null for trainer ratings if not filled
      TRAINER_QUESTIONS.forEach((q) => {
        if (!payload[q.key]) payload[q.key] = null;
      });

      await apiClient.post('/feedback', payload);
      setToast({ show: true, message: 'Feedback submitted successfully! Thank you.', type: 'success' });
      // Reload to show submitted state
      const { data } = await apiClient.get('/feedback/my');
      setExistingFeedback(data.feedback);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to submit feedback.';
      setToast({ show: true, message: msg, type: 'error' });
    }
    setSubmitting(false);
  };

  const submitted = !!existingFeedback;

  if (loading) {
    return (
      <div className="sfb-page">
        <PageHeader icon={MessageSquare} title="Feedback & Rating" description="Share your learning experience" />
        <div className="sfb-loading">
          <div className="spinner-border" role="status" />
        </div>
      </div>
    );
  }

  if (!batchInfo || !courseInfo) {
    return (
      <div className="sfb-page">
        <PageHeader icon={MessageSquare} title="Feedback & Rating" description="Share your learning experience" />
        <EmptyState
          icon={<BookOpen />}
          title="No active enrollment"
          description="You are not currently enrolled in any batch. Feedback can be submitted once you are assigned to a batch."
        />
      </div>
    );
  }

  return (
    <div className="sfb-page">
      <PageHeader icon={MessageSquare} title="Feedback & Rating" description="Share your learning experience" />

      {/* Enrollment Context Card */}
      <AppCard className="sfb-context-card">
        <AppCardContent>
          <div className="sfb-context">
            <div className="sfb-context__item">
              <BookOpen size={16} />
              <div>
                <span className="sfb-context__label">Course</span>
                <span className="sfb-context__value">{courseInfo.name}</span>
              </div>
            </div>
            <div className="sfb-context__item">
              <Users size={16} />
              <div>
                <span className="sfb-context__label">Batch</span>
                <span className="sfb-context__value">{batchInfo.name}</span>
              </div>
            </div>
            <div className="sfb-context__item">
              <Users size={16} />
              <div>
                <span className="sfb-context__label">Trainer</span>
                <span className="sfb-context__value">{batchInfo.instructorName}</span>
              </div>
            </div>
            {submitted && (
              <div className="sfb-submitted-badge">
                <CheckCircle size={16} />
                Submitted
              </div>
            )}
          </div>
        </AppCardContent>
      </AppCard>

      {/* Trainer Feedback */}
      <AppCard className="sfb-section-card">
        <AppCardHeader>
          <AppCardTitle>
            <Users size={16} />
            Trainer Feedback
            <span className="sfb-optional-tag">Optional</span>
          </AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <p className="sfb-section-desc">Your trainer feedback is confidential and will not be visible to the trainer.</p>
          <div className="sfb-questions">
            {TRAINER_QUESTIONS.map((q) => (
              <QuestionRow
                key={q.key}
                question={q.label}
                value={ratings[q.key]}
                onChange={(val) => setRating(q.key, val)}
                disabled={submitted}
              />
            ))}
          </div>
        </AppCardContent>
      </AppCard>

      {/* Course Feedback */}
      <AppCard className="sfb-section-card">
        <AppCardHeader>
          <AppCardTitle>
            <BookOpen size={16} />
            Course Feedback
            <span className="sfb-required-tag">Required</span>
          </AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="sfb-questions">
            {COURSE_QUESTIONS.map((q) => (
              <QuestionRow
                key={q.key}
                question={q.label}
                value={ratings[q.key]}
                onChange={(val) => setRating(q.key, val)}
                disabled={submitted}
              />
            ))}
          </div>
        </AppCardContent>
      </AppCard>

      {/* Suggestions */}
      <AppCard className="sfb-section-card">
        <AppCardHeader>
          <AppCardTitle>
            <MessageSquare size={16} />
            Suggestions
            <span className="sfb-optional-tag">Optional</span>
          </AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <textarea
            className="sfb-textarea"
            placeholder="Any suggestions or improvements you'd like to share..."
            rows={4}
            maxLength={2000}
            value={suggestions}
            onChange={(e) => setSuggestions(e.target.value)}
            disabled={submitted}
          />
          <div className="sfb-char-count">{suggestions.length}/2000</div>
        </AppCardContent>
      </AppCard>

      {/* Submit Button */}
      {!submitted && (
        <div className="sfb-submit-row">
          <button
            className="sfb-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || !courseRatingsValid}
          >
            {submitting ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <Send size={16} />
            )}
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      )}

      {submitted && (
        <div className="sfb-thanks">
          <CheckCircle size={22} />
          <p>Thank you for your feedback! Your response has been recorded.</p>
        </div>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default StudentFeedbackPage;
